import React from 'react';
import Isvg from 'react-inlinesvg';
import { defineMessages } from 'react-intl';
import PropTypes from 'prop-types';

import { Card } from 'components/Card';
import Message from 'components/Message';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';

import { campaignsOnConsumer, howItWorks1, howItWorks2, howItWorks3, performance } from './CampaignsList.images';

import './CampaignsList.scss';

const messages = defineMessages({
  targetForFree: {
    id: 'campaigns.how-it-works.target-for-free',
    defaultMessage: 'Target your campaigns for free',
  },
  controlWhoSees: {
    id: 'campaigns.how-it-works.control-who-sees',
    defaultMessage: 'Control who sees your offer and ensure you get noticed by the right audience.',
  },
  howItWorks1: {
    id: 'campaigns.how-it-works.how-it-works-1',
    defaultMessage: 'Attract <strong>new members</strong> who have<br></br>never visited your business',
  },
  howItWorks2: {
    id: 'campaigns.how-it-works.how-it-works-2',
    defaultMessage: 'Build loyal customers by<br></br>retargeting <strong>recent visitors</strong>',
  },
  howItWorks3: {
    id: 'campaigns.how-it-works.how-it-works-3',
    defaultMessage: 'Grow fast with a general campaign<br></br>to <strong>all members</strong>',
  },
  controlDiscount: {
    id: 'campaigns.how-it-works.control-discount',
    defaultMessage: 'Control your discount and track performance',
  },
  campaignsStandOut: {
    id: 'campaigns.how-it-works.campaigns-stand-out',
    defaultMessage: 'Control your discount and track performance',
  },
  campaignsIncreaseImpressions: {
    id: 'campaigns.how-it-works.campaigns-increase-impressions',
    defaultMessage:
      'On average, campaigns increase impressions to your page by <strong>+16%</strong> and double reservations.',
  },
  highestWillApply: {
    id: 'campaigns.how-it-works.highest-will-apply',
    defaultMessage: 'If a member is eligible for multiple campaigns, only the highest discount will apply.',
  },
  noAdditionalCost: {
    id: 'campaigns.how-it-works.no-additional-cost',
    defaultMessage: 'ClassPass highlights your campaign in Search and on your page – at no additional cost.',
  },
});

const { element } = bem('campaigns-list');

const applyStrongAndBreak = {
  strong: chunks => <strong>{chunks}</strong>,
  br: () => <br />,
};

function CarouselCardHeader({ icon, noCenter, message }) {
  return (
    <header className={element('header', { 'small-gap': true, noCenter })}>
      <Isvg src={icon} />
      <Message as="h4" className={element('heading', { smaller: true })} message={message} />
    </header>
  );
}

CarouselCardHeader.propTypes = {
  icon: PropTypes.string,
  noCenter: PropTypes.bool,
  message: PropTypes.shape({ id: PropTypes.string, defaultMessage: PropTypes.string }),
};

function CarouselInnerCard({ img, children }) {
  return (
    <Card flush className={element('inner-card')}>
      <picture className={element('inner-card-composite-picture')}>
        <img src={img} alt="" />
        <Isvg src={ICONS.classpassLogo} />
      </picture>

      <p className={element('inner-card-paragraph')}>{children}</p>
    </Card>
  );
}

CarouselInnerCard.propTypes = {
  img: PropTypes.string,
  children: PropTypes.node,
};

export function HowItWorksCard1() {
  return (
    <Card flush className={element('carousel-card')}>
      <CarouselCardHeader icon={ICONS.paper} message={messages.targetForFree} />

      <p className={element('carousel-paragraph')}>
        <Message message={messages.controlWhoSees} />
      </p>

      <CarouselInnerCard img={howItWorks1}>
        <Message message={messages.howItWorks1} values={applyStrongAndBreak} />
      </CarouselInnerCard>

      <CarouselInnerCard img={howItWorks2}>
        <Message message={messages.howItWorks2} values={applyStrongAndBreak} />
      </CarouselInnerCard>

      <CarouselInnerCard img={howItWorks3}>
        <Message message={messages.howItWorks3} values={applyStrongAndBreak} />
      </CarouselInnerCard>
    </Card>
  );
}

export function HowItWorksCard2() {
  return (
    <Card flush className={element('carousel-card')}>
      <CarouselCardHeader icon={ICONS.calendarCheckmark} noCenter message={messages.controlDiscount} />

      <p className={element('carousel-paragraph')}>
        <Message message={messages.campaignsIncreaseImpressions} values={applyStrongAndBreak} />
      </p>

      <img className={element('carousel-image')} src={performance} alt="" />

      <p className={element('carousel-paragraph', { 'extra-margin': true })}>
        <Message message={messages.highestWillApply} values={applyStrongAndBreak} />
      </p>
    </Card>
  );
}

export function HowItWorksCard3() {
  return (
    <Card flush className={element('carousel-card')}>
      <CarouselCardHeader icon={ICONS.arrowChartUp} message={messages.campaignsStandOut} />

      <p className={element('carousel-paragraph')}>
        <Message message={messages.noAdditionalCost} values={applyStrongAndBreak} />
      </p>

      <img className={element('carousel-image')} src={campaignsOnConsumer} alt="" />
    </Card>
  );
}
